import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Avatar,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Popover,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const getServiceColor = (service) => {
  const serviceMap = {
    "hair care": { bg: "#FCE7F3", color: "#DB2777", avatar: "#F472B6" },
    "skin care": { bg: "#DBEAFE", color: "#2563EB", avatar: "#3B82F6" },
    "web development": { bg: "#FEF3C7", color: "#D97706", avatar: "#F59E0B" },
    electronic: { bg: "#E0E7FF", color: "#4F46E5", avatar: "#6366F1" },
  };
  const key = Object.keys(serviceMap).find(
    (k) => k.toLowerCase() === service?.toLowerCase(),
  );
  return (
    serviceMap[key] || { bg: "#F3F4F6", color: "#6B7280", avatar: "#9CA3AF" }
  );
};

const getStatusConfig = (status) => {
  // Pipeline stages
  const statusMap = {
    won: { bg: "#ECFDF5", color: "#10B981", label: "WON" },
    hot: { bg: "#FEE2E2", color: "#DC2626", label: "HOT" },
    warm: { bg: "#FEF3C7", color: "#D97706", label: "WARM" },
    cold: { bg: "#F3F4F6", color: "#6B7280", label: "COLD" },
    new: { bg: "#E0F2FE", color: "#0284C7", label: "NEW" },
    qualified: { bg: "#D1FAE5", color: "#059669", label: "QUALIFIED" },
    booked: { bg: "#C7D2FE", color: "#4F46E5", label: "BOOKED" },
    proposal_shared: { bg: "#F3E8FF", color: "#9333EA", label: "PROPOSAL" },
    negotiation: { bg: "#FEF3C7", color: "#D97706", label: "NEGOTIATION" },
    treatment_booked: { bg: "#DBEAFE", color: "#2563EB", label: "TREATMENT" },
    post_consult: { bg: "#E0E7FF", color: "#4F46E5", label: "POST CONSULT" },
    need_analysis: { bg: "#FED7AA", color: "#EA580C", label: "ANALYSIS" },
    dropped: { bg: "#FEE2E2", color: "#DC2626", label: "DROPPED" },
  };

  // Call outcome / other statuses (grouped as OTHERS)
  const otherStatuses = [
    "not_connected", "not-connected", "rnr", "voicemail", "spam", "wrong_number", "wrong-number", "wrongnumber",
    "busy", "switched_off", "invalid", "unknown"
  ];

  const key = status?.toLowerCase().replace(/ /g, "_").replace(/-/g, "_");

  // Check if it's a known status
  if (statusMap[key]) {
    return statusMap[key];
  }

  // For call outcome and unknown statuses - route to leads (grade) but show as OTHERS or the actual name
  const isCallOutcome = otherStatuses.includes(key);
  const displayLabel = isCallOutcome ? "OTHERS" : (status?.toUpperCase() || "UNKNOWN");

  return {
    bg: isCallOutcome ? "#F3F4F6" : "#FEE2E2",
    color: isCallOutcome ? "#6B7280" : "#DC2626",
    label: displayLabel,
    routeAsGrade: true, // These should route to /leads
  };
};

const getPriorityConfig = (priority) => {
  const priorityMap = {
    HIGH: { color: "#DC2626", label: "HIGH", dot: "#EF4444" },
    MEDIUM: { color: "#D97706", label: "MEDIUM", dot: "#F59E0B" },
    LOW: { color: "#059669", label: "LOW", dot: "#10B981" },
  };
  return (
    priorityMap[priority] || {
      color: "#6B7280",
      label: "UNKNOWN",
      dot: "#9CA3AF",
    }
  );
};

// Grade stages (leads)
const gradeStages = ["new", "cold", "hot", "warm", "dropped"];

const DSPLeadsTable = ({ data = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
  });

  const rowsPerPage = 10;
  const open = Boolean(filterAnchorEl);

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
  };

  // Filter data based on search and filters
  const filteredData = data.filter((row) => {
    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      row.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.contact?.includes(searchTerm) ||
      row.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus =
      filters.status.length === 0 ||
      filters.status.includes(row.current_status?.key?.toLowerCase());

    // Priority filter
    const matchesPriority =
      filters.priority.length === 0 ||
      filters.priority.includes(row.priority?.toLowerCase());

    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const formatDate = (dateString) => {
    if (!dateString) return { date: "-", time: "-" };
    const date = dayjs(dateString);
    return {
      date: date.format("MMM D, YYYY").toUpperCase(),
      time: date.format("h:mm A").toUpperCase(),
    };
  };

  const getInitials = (name) => {
    if (!name) return "N";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleViewClick = (lead) => {
    const statusType = lead.current_status?.type;
    const leadId = lead._id;

    if (
      statusType === "grade" ||
      gradeStages.includes(lead.current_status?.key)
    ) {
      navigate(`/leads/${leadId}`);
    } else {
      navigate(`/pipelines/${leadId}`);
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
    setPage(1);
  };

  const togglePriorityFilter = (priority) => {
    setFilters((prev) => ({
      ...prev,
      priority: prev.priority.includes(priority)
        ? prev.priority.filter((p) => p !== priority)
        : [...prev.priority, priority],
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: [], priority: [] });
    setPage(1);
  };

  const hasActiveFilters =
    filters.status.length > 0 || filters.priority.length > 0;

  const statusOptions = [
    "new",
    "hot",
    "warm",
    "cold",
    "qualified",
    "booked",
    "won",
  ];
  const priorityOptions = ["high", "medium", "low"];

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: colors.textMuted,
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            LEADS DETAILED VIEW
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.textSecondary,
            }}
          >
            {filteredData.length} LEADS FOUND
            {data.length !== filteredData.length && ` (${data.length} TOTAL)`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <TextField
            placeholder="SEARCH LEADS..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 18, color: colors.textMuted }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ mr: -0.5 }}
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: hasActiveFilters
                    ? theme.palette.primary.main
                    : colors.borderLight,
                },
              },
            }}
          />
          <IconButton
            onClick={handleFilterClick}
            sx={{
              border: `1px solid ${hasActiveFilters ? theme.palette.primary.main : colors.borderLight}`,
              borderRadius: 2,
              color: hasActiveFilters
                ? theme.palette.primary.main
                : colors.textMuted,
              bgcolor: hasActiveFilters
                ? `${theme.palette.primary.main}10`
                : "transparent",
            }}
          >
            <FilterListIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Filter Popover */}
      <Popover
        open={open}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Stack spacing={2}>
          {/* Status Filter */}
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textPrimary,
                mb: 1,
              }}
            >
              STATUS
            </Typography>
            <Stack spacing={0.5}>
              {statusOptions.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.status.includes(status)}
                      onChange={() => toggleStatusFilter(status)}
                      sx={{
                        color: theme.palette.primary.main,
                        "&.Mui-checked": { color: theme.palette.primary.main },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: 12, textTransform: "capitalize" }}
                    >
                      {status}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Box>

          {/* Priority Filter */}
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textPrimary,
                mb: 1,
              }}
            >
              PRIORITY
            </Typography>
            <Stack spacing={0.5}>
              {priorityOptions.map((priority) => (
                <FormControlLabel
                  key={priority}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.priority.includes(priority)}
                      onChange={() => togglePriorityFilter(priority)}
                      sx={{
                        color: theme.palette.primary.main,
                        "&.Mui-checked": { color: theme.palette.primary.main },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: 12, textTransform: "capitalize" }}
                    >
                      {priority}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Box>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{
                textTransform: "none",
                fontSize: 12,
                color: theme.palette.primary.main,
                fontWeight: 600,
                p: 0.5,
                "&:hover": { bgcolor: `${theme.palette.primary.main}10` },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Popover>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>DATE ADDED</TableCell>
            <TableCell sx={headerCellSx}>LEAD NAME</TableCell>
            <TableCell sx={headerCellSx}>CONTACT</TableCell>
            <TableCell sx={headerCellSx}>SERVICES</TableCell>
            <TableCell sx={headerCellSx}>STATUS</TableCell>
            <TableCell sx={headerCellSx}>PRIORITY</TableCell>
            <TableCell sx={headerCellSx}>REVENUE</TableCell>
            <TableCell sx={headerCellSx} align="center">
              ACTION
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ py: 8, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                  {searchTerm || hasActiveFilters
                    ? "NO LEADS MATCH YOUR FILTERS"
                    : "NO LEADS DATA AVAILABLE FOR SELECTED PERIOD"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((row) => {
              const statusConfig = getStatusConfig(row.current_status?.key);
              const priorityConfig = getPriorityConfig(row.priority);
              const dateTime = formatDate(row.created_at);

              return (
                <TableRow
                  key={row._id}
                  sx={{
                    "&:hover": {
                      bgcolor: colors.bgPrimary,
                    },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{ fontSize: 11, color: colors.textSecondary }}
                    >
                      {dateTime.date}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: colors.textMuted }}>
                      {dateTime.time}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(row.client_name)}
                      </Avatar>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {row.client_name?.toUpperCase() || "-"}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="column" spacing={0.5}>
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        {row.contact || "-"}
                      </Typography>
                      {row.email && (
                        <Typography
                          sx={{ fontSize: 11, color: colors.textMuted }}
                        >
                          {row.email.toUpperCase()}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {row.services?.length > 0 ? (
                      <Stack direction="row" spacing={-0.8} alignItems="center">
                        {/* Show first 3 services as avatars with overlay effect */}
                        {row.services.slice(0, 3).map((service, idx) => (
                          <Tooltip
                            key={idx}
                            title={
                              service.charAt(0).toUpperCase() + service.slice(1)
                            }
                            arrow
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: getServiceColor(service).avatar,
                                color: "#FFF",
                                border: "2px solid #FFF",
                                boxShadow: `0 2px 8px ${alpha(getServiceColor(service).avatar, 0.4)}`,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  transform: "scale(1.1) translateY(-2px)",
                                  boxShadow: `0 6px 16px ${alpha(getServiceColor(service).avatar, 0.6)}`,
                                },
                              }}
                            >
                              {service.substring(0, 2).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        ))}
                        {row.services.length > 3 && (
                          <Tooltip
                            title={row.services.slice(3).join(", ")}
                            arrow
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: "#F3F4F6",
                                color: "#6B7280",
                                border: "2px solid #FFF",
                                boxShadow: `0 2px 8px ${alpha("#9CA3AF", 0.3)}`,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  transform: "scale(1.1) translateY(-2px)",
                                  boxShadow: `0 6px 16px ${alpha("#9CA3AF", 0.5)}`,
                                },
                              }}
                            >
                              +{row.services.length - 3}
                            </Avatar>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: 12 }}>-</Typography>
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={statusConfig.label}
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        height: 24,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: priorityConfig.dot,
                          boxShadow: `0 0 6px ${priorityConfig.dot}`,
                        }}
                      />
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        {priorityConfig.label}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {row.totalPayment > 0
                        ? `₹${row.totalPayment.toLocaleString()}`
                        : "-"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx} align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewClick(row)}
                      sx={{
                        px: 2,
                        py: 0.5,
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        borderRadius: 2,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: `${theme.palette.primary.main}10`,
                          borderColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      VIEW
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mt={3}
        >
          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
            SHOWING {(page - 1) * rowsPerPage + 1} -{" "}
            {Math.min(page * rowsPerPage, filteredData.length)} OF{" "}
            {filteredData.length} LEADS
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 1,
                color: colors.textMuted,
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  size="small"
                  onClick={() => setPage(pageNum)}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    px: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor:
                      page === pageNum
                        ? theme.palette.primary.main
                        : "transparent",
                    color: page === pageNum ? "#FFF" : colors.textSecondary,
                    border:
                      page === pageNum
                        ? "none"
                        : `1px solid ${colors.borderLight}`,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor:
                        page === pageNum
                          ? theme.palette.primary.dark
                          : colors.bgPrimary,
                    },
                  }}
                >
                  {pageNum}
                </Button>
              );
            })}

            <IconButton
              size="small"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              sx={{
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 1,
                color: colors.textMuted,
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default DSPLeadsTable;
